import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SimpalloAuthUser } from './types';

import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react';

import { jwtDecode as jwt_decode } from 'jwt-decode';

export type AuthContextData = {
  permissions: string[];
  user: SimpalloAuthUser | null;
  login: (...args: any[]) => Promise<void>;
  token: string | null;
  logout: (...args: any[]) => any;
  isLoading: boolean;
  getToken: (
    options?: GetTokenSilentlyOptions & { detailedResponse: true },
  ) => Promise<string>;

  reloadToken: () => Promise<string>;
};

export const CoreAuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
);

export const AuthInternalProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const auth = useAuth0<SimpalloAuthUser>();
  const [lastToken, setToken] = useState<string | null>(null);

  const [lastTokenUpdate, setlastTokenUpdate] = useState(auth.user || null);
  useEffect(() => {
    if (!lastToken) {
      return;
    }
    try {
      setlastTokenUpdate(jwt_decode(lastToken) || null);
    } catch (err) {
      console.warn('[Auth] Failed to decode token:', err);
      setlastTokenUpdate(null);
    }
  }, [lastToken]);
  const login = auth.loginWithRedirect;
  const logout = auth.logout;
  const { user: userOG, isLoading, getAccessTokenSilently } = auth;
  // Token was not updating meta on token refresh's
  const user = useMemo(() => {
    if (!userOG) {
      return undefined;
    }
    const userMeta =
      userOG['https://core-backend.fence-estimator/app_metadata'] || {};
    const lastTokenMeta =
      lastTokenUpdate?.['https://core-backend.fence-estimator/app_metadata'] ||
      {};

    return {
      ...userOG,
      'https://core-backend.fence-estimator/app_metadata': {
        ...userMeta,
        ...lastTokenMeta,
      },
    } as SimpalloAuthUser;
  }, [JSON.stringify(userOG), JSON.stringify(lastTokenUpdate)]);

  const permissions: string[] = useMemo(() => {
    return lastTokenUpdate?.permissions;
  }, [JSON.stringify(lastTokenUpdate)]);

  return (
    <CoreAuthContext.Provider
      value={{
        permissions,
        user: user || null,
        login,
        token: lastToken || null,
        logout,
        isLoading,
        getToken: async (
          options?: GetTokenSilentlyOptions & { detailedResponse: true },
        ) => {
          try {
            const token = await getAccessTokenSilently({
              ...options,
            });
            try {
              setToken(token);
            } catch {}
            return token;
          } catch (err: any) {
            // Set flag to prevent redirect loop when login_required
            if (err?.error === 'login_required') {
              sessionStorage.setItem('auth0_login_required', 'true');
            }
            throw err;
          }
        },
        reloadToken: async () => {
          try {
            const token = await getAccessTokenSilently({ cacheMode: 'off' });
            try {
              setToken(token);
            } catch {}
            return token;
          } catch (err: any) {
            // In incognito, refresh might fail - try cached token as fallback
            if (err?.error === 'login_required') {
              try {
                const cachedToken = await getAccessTokenSilently();
                setToken(cachedToken);
                return cachedToken;
              } catch {
                // No cached token either
              }
            }
            throw err;
          }
        },
      }}>
      {children}
    </CoreAuthContext.Provider>
  );
};
